// Laboratorio: solicitudes y resultados. Centro Medico → Clínica → Laboratorio.
// Solicitud electrónica de exámenes, captura de resultados con marcado
// automático alto/bajo, entrega y gráfico evolutivo por parámetro.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Ban, CheckCircle2, FlaskConical, History, LineChart as LineIcon, Loader2, Plus, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  Campo, ESTADO_SOLICITUD, Examen, FLAG_CLS, HistorialResultado, MedicoRow, Modal, PacienteRow, ResultadoValor,
  SerieRow, SolicitudDetalle, SolicitudItem, SolicitudRow, fieldCls, hhmm, medRpc,
} from './shared';
import { useAuth } from '@/lib/hooks';
import {
  EncabezadoImpreso, FirmaImpreso, NOMBRE_CATEGORIA, edadTexto, fechaPais, tituloOrden,
} from './impreso';
import { LOCALE } from '@/lib/utils/regional';

const ESTADOS = ['solicitada', 'en_proceso', 'lista', 'entregada'];

export function Laboratorio() {
  const qc = useQueryClient();
  const [modo, setModo] = useState<'bandeja' | 'historial'>('bandeja');
  const [estado, setEstado] = useState('solicitada');
  const { data: rows = [], isFetching } = useQuery<SolicitudRow[]>({
    queryKey: ['med-solicitudes', estado], queryFn: () => medRpc<SolicitudRow[]>('med_solicitudes', { _estado: estado, _paciente_id: null }), enabled: modo === 'bandeja',
  });
  const [nueva, setNueva] = useState(false);
  const [detalle, setDetalle] = useState<string | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-solicitudes'] });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical className="w-5 h-5" /> Laboratorio</h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border overflow-hidden">
            <button onClick={() => setModo('bandeja')} aria-pressed={modo === 'bandeja'} className={`px-3 py-1.5 text-sm ${modo === 'bandeja' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Bandeja</button>
            <button onClick={() => setModo('historial')} aria-pressed={modo === 'historial'} className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${modo === 'historial' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><History className="w-4 h-4" /> Historial del paciente</button>
          </div>
          {modo === 'bandeja' && <Button onClick={() => setNueva(true)}><Plus className="w-4 h-4 mr-1" /> Nueva solicitud</Button>}
        </div>
      </div>

      {modo === 'historial' ? <HistorialExamenes /> : (<>
      <div className="inline-flex rounded-md border overflow-hidden">
        {ESTADOS.map((s) => (
          <button key={s} onClick={() => setEstado(s)} aria-pressed={estado === s} className={`px-3 py-1.5 text-sm ${estado === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{ESTADO_SOLICITUD[s]?.label ?? s}</button>
        ))}
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Paciente</th>
              <th className="py-2 pr-3">Médico</th><th className="py-2 pr-3">Exámenes</th><th className="py-2 pr-3">Prioridad</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin solicitudes</td></tr>}
              {rows.map((r) => {
                const e = ESTADO_SOLICITUD[r.estado];
                return (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" role="button" tabIndex={0} onClick={() => setDetalle(r.id)} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setDetalle(r.id); } }}>
                    <td className="py-2 pr-3 font-medium">{r.codigo}</td>
                    <td className="py-2 pr-3">{new Date(r.fecha).toLocaleDateString(LOCALE)} {hhmm(r.fecha.slice(11))}</td>
                    <td className="py-2 pr-3">{r.paciente}</td>
                    <td className="py-2 pr-3">{r.medico}</td>
                    <td className="py-2 pr-3">{r.items}</td>
                    <td className="py-2 pr-3">{r.prioridad === 'urgente' ? <span className="text-urgente font-medium">Urgente</span> : 'Rutina'}</td>
                    <td className="py-2 pr-3"><span className={`rounded px-2 py-0.5 text-xs ${e?.cls ?? ''}`}>{e?.label ?? r.estado}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      </>)}

      {nueva && <NuevaSolicitud onClose={() => setNueva(false)} onSaved={() => { setNueva(false); refrescar(); }} />}
      {detalle && <Detalle id={detalle} onClose={() => setDetalle(null)} onChanged={refrescar} />}
    </div>
  );
}

// ---- Historial de exámenes del paciente (doble vista: cronológica / acumulada) ----
function HistorialExamenes() {
  const [pacienteId, setPacienteId] = useState('');
  const [vista, setVista] = useState<'cronologica' | 'acumulada'>('cronologica');
  const [grafico, setGrafico] = useState<string | null>(null);
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: rows = [], isFetching } = useQuery<HistorialResultado[]>({
    queryKey: ['med-historial-res', pacienteId], queryFn: () => medRpc<HistorialResultado[]>('med_historial_resultados', { _paciente_id: pacienteId }), enabled: !!pacienteId,
  });

  // Vista cronológica: agrupada por solicitud (fecha + código).
  const porSolicitud = useMemo(() => {
    const m = new Map<string, { codigo: string; fecha: string; items: HistorialResultado[] }>();
    for (const r of rows) {
      const k = r.solicitud_id;
      if (!m.has(k)) m.set(k, { codigo: r.codigo, fecha: r.fecha, items: [] });
      m.get(k)!.items.push(r);
    }
    return [...m.values()].sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [rows]);

  // Vista acumulada: pivote parámetro × fecha (reporte columnar tipo laboratorio).
  const pivote = useMemo(() => {
    const fechas = [...new Set(rows.map((r) => r.fecha))].sort((a, b) => b.localeCompare(a)).slice(0, 12);
    const params = new Map<string, { examen: string; unidad: string | null; ref: string | null; celdas: Record<string, HistorialResultado> }>();
    for (const r of rows) {
      if (!params.has(r.parametro)) params.set(r.parametro, {
        examen: r.examen, unidad: r.unidad,
        ref: r.valor_ref_texto ?? ((r.valor_ref_min != null || r.valor_ref_max != null) ? `${r.valor_ref_min ?? ''}–${r.valor_ref_max ?? ''}` : null),
        celdas: {},
      });
      if (fechas.includes(r.fecha)) params.get(r.parametro)!.celdas[r.fecha] = r;
    }
    return { fechas, params: [...params.entries()].sort((a, b) => a[0].localeCompare(b[0])) };
  }, [rows]);

  const valorTxt = (r: HistorialResultado) => r.valor != null ? String(r.valor) : (r.valor_texto ?? '—');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select className={`${fieldCls} w-72`} value={pacienteId} onChange={(e) => setPacienteId(e.target.value)}>
          <option value="">— Selecciona un paciente —</option>
          {pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
        </select>
        {pacienteId && (
          <div className="inline-flex rounded-md border overflow-hidden">
            <button onClick={() => setVista('cronologica')} aria-pressed={vista === 'cronologica'} className={`px-3 py-1.5 text-sm ${vista === 'cronologica' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Cronológica</button>
            <button onClick={() => setVista('acumulada')} aria-pressed={vista === 'acumulada'} className={`px-3 py-1.5 text-sm ${vista === 'acumulada' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>Acumulada</button>
          </div>
        )}
      </div>

      {!pacienteId && <Card><CardContent className="py-12 text-center text-muted-foreground">Selecciona un paciente para ver su historial de exámenes.</CardContent></Card>}
      {pacienteId && (
        <Card>
          <CardContent className="overflow-x-auto pt-4">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            {!isFetching && rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Sin resultados registrados para este paciente.</p>}

            {!isFetching && rows.length > 0 && vista === 'cronologica' && (
              <div className="space-y-4">
                {porSolicitud.map((g) => (
                  <div key={g.codigo} className="rounded-md border">
                    <div className="bg-muted/40 px-3 py-1.5 text-sm font-medium flex items-center justify-between">
                      <span>{g.codigo}</span><span className="text-xs text-muted-foreground">{new Date(g.fecha).toLocaleString(LOCALE)}</span>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {g.items.map((r, i) => (
                          <tr key={i} className="border-t">
                            <td className="py-1.5 px-3">{r.examen} · <span className="text-muted-foreground">{r.parametro}</span></td>
                            <td className="py-1.5 px-3 text-right tabular-nums font-medium">{valorTxt(r)} {r.unidad ?? ''}</td>
                            <td className="py-1.5 px-3 text-xs text-muted-foreground">Ref: {r.valor_ref_texto ?? `${r.valor_ref_min ?? ''}–${r.valor_ref_max ?? ''}`}</td>
                            <td className={`py-1.5 px-3 text-xs ${FLAG_CLS[r.flag] ?? ''}`}>{r.flag !== 'normal' ? r.flag : ''}</td>
                            <td className="py-1.5 px-3 text-right"><button className="text-muted-foreground hover:text-primary" title="Gráfico evolutivo" aria-label="Gráfico evolutivo" onClick={() => setGrafico(r.parametro)}><LineIcon className="w-4 h-4" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}

            {!isFetching && rows.length > 0 && vista === 'acumulada' && (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 sticky left-0 bg-background">Parámetro</th>
                  <th className="py-2 pr-3">Ref.</th>
                  {pivote.fechas.map((f) => <th key={f} className="py-2 px-2 text-right whitespace-nowrap">{new Date(f).toLocaleDateString(LOCALE)}</th>)}
                  <th></th>
                </tr></thead>
                <tbody>
                  {pivote.params.map(([nombre, p]) => (
                    <tr key={nombre} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 pr-3 sticky left-0 bg-background"><span className="font-medium">{nombre}</span>{p.unidad ? <span className="text-muted-foreground"> ({p.unidad})</span> : ''}<div className="text-[11px] text-muted-foreground">{p.examen}</div></td>
                      <td className="py-1.5 pr-3 text-xs text-muted-foreground whitespace-nowrap">{p.ref ?? '—'}</td>
                      {pivote.fechas.map((f) => {
                        const r = p.celdas[f];
                        return <td key={f} className={`py-1.5 px-2 text-right tabular-nums ${r && r.flag !== 'normal' ? FLAG_CLS[r.flag] ?? '' : ''}`}>{r ? valorTxt(r) : '·'}</td>;
                      })}
                      <td className="py-1.5 pl-2 text-right"><button className="text-muted-foreground hover:text-primary" title="Gráfico evolutivo" aria-label="Gráfico evolutivo" onClick={() => setGrafico(nombre)}><LineIcon className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {grafico && pacienteId && <GraficoPaciente pacienteId={pacienteId} parametro={grafico} onClose={() => setGrafico(null)} />}
    </div>
  );
}

// Gráfico evolutivo por paciente+parámetro (historial), reutiliza med_serie_resultados.
function GraficoPaciente({ pacienteId, parametro, onClose }: { pacienteId: string; parametro: string; onClose: () => void }) {
  const { data: serie = [], isFetching } = useQuery<SerieRow[]>({
    queryKey: ['med-serie-pac', pacienteId, parametro],
    queryFn: () => medRpc<SerieRow[]>('med_serie_resultados', { _paciente_id: pacienteId, _parametro: parametro }),
  });
  const data = serie.map((s) => ({ ...s, f: new Date(s.fecha).toLocaleDateString(LOCALE) }));
  const ref = serie.find((s) => s.valor_ref_min != null || s.valor_ref_max != null);
  return (
    <Modal title={`Evolución · ${parametro}`} onClose={onClose} wide>
      <div className="h-72">
        {isFetching ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          : data.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Sin datos históricos para «{parametro}».</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                {ref && ref.valor_ref_min != null && ref.valor_ref_max != null && (
                  <ReferenceArea y1={ref.valor_ref_min} y2={ref.valor_ref_max} fill="#22c55e" fillOpacity={0.1} />
                )}
                <XAxis dataKey="f" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
                <Line type="monotone" dataKey="valor" name={parametro} stroke="#3b82f6" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          )}
      </div>
    </Modal>
  );
}

// ---- Nueva solicitud ----
export function NuevaSolicitud({ onClose, onSaved, pacienteInicial, diagnosticoInicial }: {
  onClose: () => void; onSaved: () => void; pacienteInicial?: string; diagnosticoInicial?: string;
}) {
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const { data: examenes = [] } = useQuery<Examen[]>({ queryKey: ['med-examenes'], queryFn: () => medRpc<Examen[]>('med_examenes', { _busqueda: '' }) });
  // El médico solicitante es el usuario logueado (no se elige). Admin/recepción (sin médico) mantiene el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';

  const [f, setF] = useState({ paciente_id: pacienteInicial ?? '', medico_id: '', prioridad: 'rutina', laboratorio: '', diagnostico: diagnosticoInicial ?? '', notas: '' });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const guardar = useMutation({
    mutationFn: () => medRpc('med_crear_solicitud', {
      _data: { ...f, medico_id: miMedicoId || f.medico_id, laboratorio: f.laboratorio || null, diagnostico: f.diagnostico || null, notas: f.notas || null, examenes: [...sel].map((id) => ({ examen_id: id })) },
    }),
    onSuccess: () => { toast.success('Solicitud creada'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Nueva solicitud de exámenes" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Paciente *">
            <select className={fieldCls} value={f.paciente_id} onChange={(e) => setF({ ...f, paciente_id: e.target.value })}>
              <option value="">— Selecciona —</option>{pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
            </select>
          </Campo>
          <Campo label="Médico *">
            {miMedicoId ? (
              <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
            ) : (
              <select className={fieldCls} value={f.medico_id} onChange={(e) => setF({ ...f, medico_id: e.target.value })}>
                <option value="">— Selecciona —</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
              </select>
            )}
          </Campo>
          <Campo label="Prioridad">
            <select className={fieldCls} value={f.prioridad} onChange={(e) => setF({ ...f, prioridad: e.target.value })}>
              <option value="rutina">Rutina</option><option value="urgente">Urgente</option>
            </select>
          </Campo>
          <Campo label="Laboratorio (opcional)"><input className={fieldCls} value={f.laboratorio} onChange={(e) => setF({ ...f, laboratorio: e.target.value })} placeholder="Interno / externo" /></Campo>
        </div>
        <Campo label="Diagnóstico presuntivo"><input className={fieldCls} value={f.diagnostico} onChange={(e) => setF({ ...f, diagnostico: e.target.value })} /></Campo>

        <div>
          <h4 className="text-sm font-semibold text-muted-foreground mb-1">Exámenes ({sel.size} seleccionados)</h4>
          <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
            {examenes.length === 0 && <p className="p-3 text-sm text-muted-foreground">No hay exámenes en el catálogo. Créalos en Catálogos → Catálogo de Exámenes.</p>}
            {examenes.map((ex) => (
              <label key={ex.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50">
                <input type="checkbox" checked={sel.has(ex.id)} onChange={() => toggle(ex.id)} className="h-4 w-4" />
                <span className="flex-1">{ex.nombre} <span className="text-muted-foreground capitalize">· {ex.categoria}</span></span>
                <span className="text-muted-foreground">${Number(ex.precio).toFixed(2)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.paciente_id || !(miMedicoId || f.medico_id) || sel.size === 0} onClick={() => guardar.mutate()}>Crear solicitud</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Detalle de solicitud (captura de resultados) ----
function Detalle({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isFetching } = useQuery<SolicitudDetalle>({ queryKey: ['med-solicitud', id], queryFn: () => medRpc<SolicitudDetalle>('med_solicitud', { _id: id }) });
  const [grafico, setGrafico] = useState<string | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const refetch = () => { void qc.invalidateQueries({ queryKey: ['med-solicitud', id] }); onChanged(); };

  const entregar = useMutation({ mutationFn: () => medRpc('med_entregar_solicitud', { _id: id }), onSuccess: () => { toast.success('Solicitud entregada'); refetch(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const anular = useMutation({ mutationFn: () => medRpc('med_anular_solicitud', { _id: id, _motivo: 'Anulada' }), onSuccess: () => { toast.success('Solicitud anulada'); refetch(); }, onError: (e) => toast.error(getErrorMessage(e)) });

  if (isFetching || !data) return <Modal title="Solicitud" onClose={onClose}><div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div></Modal>;
  const s = data.solicitud;
  const e = ESTADO_SOLICITUD[s.estado];

  return (
    <Modal title={`Solicitud ${s.codigo}`} onClose={onClose} wide>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">{data.paciente} <span className="text-muted-foreground">({data.paciente_ident ?? 's/i'})</span></div>
            <div className="text-muted-foreground text-xs">{data.medico} · {new Date(s.fecha).toLocaleString(LOCALE)}{s.diagnostico ? ` · ${s.diagnostico}` : ''}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setImprimir(true)}>
              <Printer className="w-4 h-4 mr-1" /> Imprimir orden
            </Button>
            <span className={`rounded px-2 py-0.5 text-xs ${e?.cls ?? ''}`}>{e?.label ?? s.estado}</span>
          </div>
        </div>

        {imprimir && <OrdenPrint data={data} onClose={() => setImprimir(false)} />}

        {data.items.map((it) => (
          <ItemCaptura key={it.item.id} item={it} bloqueado={s.estado === 'entregada' || s.estado === 'anulada'} onSaved={refetch}
            onGrafico={(param) => setGrafico(param)} solicitudId={id} />
        ))}

        {s.estado !== 'entregada' && s.estado !== 'anulada' && (
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="destructive" size="sm" onClick={async () => { if (!(await dlg.confirmar('¿Anular la solicitud?', { peligro: true }))) return; anular.mutate(); }}><Ban className="w-4 h-4 mr-1" /> Anular</Button>
            {s.estado === 'lista' && <Button size="sm" isLoading={entregar.isPending} onClick={() => entregar.mutate()}><CheckCircle2 className="w-4 h-4 mr-1" /> Entregar</Button>}
          </div>
        )}
      </div>
      {grafico && <GraficoSerie solicitudId={id} parametro={grafico} onClose={() => setGrafico(null)} />}
    </Modal>
  );
}

// captura de resultados de un ítem
// ---- Impreso de la orden de exámenes ----
// Misma cabecera y misma firma que la receta (features/medical/impreso.tsx):
// el médico entrega al paciente un papel reconocible, y en Perú o Colombia sale
// igual salvo las etiquetas del país. Los exámenes van agrupados por categoría
// porque el paciente los lleva a sitios distintos: el laboratorio no hace la
// ecografía.
function OrdenPrint({ data, onClose }: { data: SolicitudDetalle; onClose: () => void }) {
  const { user } = useAuth();
  const s = data.solicitud;
  const categorias = data.items.map((it) => it.categoria ?? 'laboratorio');
  const grupos = Array.from(new Set(categorias));
  const urgente = s.prioridad && s.prioridad !== 'rutina';

  return (
    <Modal title={`Orden ${s.codigo}`} onClose={onClose} wide>
      <style>{`@media print { @page { size: A4 portrait; margin: 12mm; } body * { visibility: hidden !important; } #orden-print, #orden-print * { visibility: visible !important; } #orden-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; } .no-print { display: none !important; } }`}</style>

      <div id="orden-print" className="bg-white text-black">
        <EncabezadoImpreso
          cab={data} logo={user?.empresa?.logo} fecha={s.fecha}
          derecha={<><b>Orden #</b> {s.codigo}</>}
          paciente={(data.paciente || '').toUpperCase() + (s.estado === 'anulada' ? ' (ANULADA)' : '')}
          identificacion={data.paciente_ident}
          edad={edadTexto(data.paciente_edad, data.paciente_edad_meses)}
        />

        <p className="my-2 text-center text-[13px] font-bold tracking-wide">{tituloOrden(categorias)}</p>

        {s.diagnostico && (
          <div className="mb-1.5 text-[11px]">
            <p className="font-bold">DIAGNÓSTICO / MOTIVO:</p>
            <p>{s.diagnostico}</p>
          </div>
        )}

        {grupos.map((cat) => (
          <div key={cat} className="mb-1.5 text-[11px]">
            <p className="font-bold">{(NOMBRE_CATEGORIA[cat] ?? cat).toUpperCase()}:</p>
            {data.items.filter((it) => (it.categoria ?? 'laboratorio') === cat).map((it, i) => (
              <p key={it.item.id}>
                {i + 1}. {it.nombre ?? it.item.examen_nombre}
                {it.codigo ? ` (${it.codigo})` : ''}
                {it.tipo_muestra ? ` — muestra: ${it.tipo_muestra}` : ''}
              </p>
            ))}
          </div>
        ))}

        {/* La preparación es lo que el paciente hace mal si nadie se lo escribe. */}
        {data.items.some((it) => it.preparacion) && (
          <div className="mb-1.5 text-[11px]">
            <p className="font-bold">PREPARACIÓN:</p>
            {data.items.filter((it) => it.preparacion).map((it) => (
              <p key={`prep-${it.item.id}`}>{it.nombre ?? it.item.examen_nombre}: {it.preparacion}</p>
            ))}
          </div>
        )}

        {(urgente || s.laboratorio || s.notas) && (
          <div className="mb-1.5 text-[11px]">
            <p className="font-bold">INDICACIONES:</p>
            {urgente && <p>Prioridad: {String(s.prioridad).toUpperCase()}</p>}
            {s.laboratorio && <p>Realizar en: {s.laboratorio}</p>}
            {s.notas && <p>{s.notas}</p>}
          </div>
        )}

        <FirmaImpreso
          pais={data.empresa_pais}
          medico={data.medico}
          licencia={data.medico_licencia}
          especialidad={data.especialidad}
        />
      </div>

      <div className="no-print mt-3 flex items-center justify-end gap-3 border-t pt-3">
        <span className="mr-auto text-xs text-muted-foreground">
          Emitida el {fechaPais(s.fecha, data.empresa_pais)}
        </span>
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
        <Button onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
      </div>
    </Modal>
  );
}

function ItemCaptura({ item, bloqueado, onSaved, onGrafico, solicitudId }: {
  item: SolicitudItem; bloqueado: boolean; onSaved: () => void; onGrafico: (parametro: string) => void; solicitudId: string;
}) {
  // estado local de valores: por parametro (o uno libre si no hay parametros)
  const existentes = (pnombre: string): ResultadoValor | undefined => item.resultados.find((r) => r.parametro_nombre === pnombre);
  const init: Record<string, string> = {};
  (item.parametros.length ? item.parametros.map((p) => p.nombre) : ['Resultado']).forEach((n) => { init[n] = existentes(n)?.valor?.toString() ?? existentes(n)?.valor_texto ?? ''; });
  const [vals, setVals] = useState<Record<string, string>>(init);
  const [url, setUrl] = useState(item.item.resultado_url ?? '');

  const guardar = useMutation({
    mutationFn: () => {
      const valores = item.parametros.length
        ? item.parametros.map((p) => ({
            parametro_id: p.id, parametro_nombre: p.nombre, unidad: p.unidad,
            valor_ref_min: p.valor_ref_min, valor_ref_max: p.valor_ref_max, valor_ref_texto: p.valor_ref_texto,
            valor: /^-?\d+(\.\d+)?$/.test(vals[p.nombre] ?? '') ? vals[p.nombre] : null,
            valor_texto: /^-?\d+(\.\d+)?$/.test(vals[p.nombre] ?? '') ? null : (vals[p.nombre] || null),
          }))
        : [{ parametro_nombre: item.item.examen_nombre, valor: null, valor_texto: vals['Resultado'] || null }];
      return medRpc('med_registrar_resultados', { _data: { solicitud_item_id: item.item.id, resultado_url: url || null, valores } });
    },
    onSuccess: () => { toast.success('Resultados guardados'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium">{item.item.examen_nombre}</span>
        <span className="text-xs text-muted-foreground">{item.item.estado}</span>
      </div>
      {item.parametros.length > 0 ? (
        <div className="space-y-1.5">
          {item.parametros.map((p) => {
            const res = existentes(p.nombre);
            return (
              <div key={p.id} className="grid grid-cols-12 items-center gap-2 text-sm">
                <span className="col-span-4">{p.nombre} {p.unidad && <span className="text-muted-foreground">({p.unidad})</span>}</span>
                <span className="col-span-3 text-xs text-muted-foreground">Ref: {p.valor_ref_texto ?? `${p.valor_ref_min ?? ''}–${p.valor_ref_max ?? ''}`}</span>
                <input disabled={bloqueado} className={`${fieldCls} col-span-3 h-9`} value={vals[p.nombre] ?? ''} onChange={(e) => setVals({ ...vals, [p.nombre]: e.target.value })} />
                <span className={`col-span-1 text-xs ${FLAG_CLS[res?.flag ?? 'normal']}`}>{res?.flag && res.flag !== 'normal' ? res.flag : ''}</span>
                <button type="button" className="col-span-1 text-muted-foreground hover:text-primary" title="Gráfico evolutivo" aria-label="Gráfico evolutivo" onClick={() => onGrafico(p.nombre)}><LineIcon className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      ) : (
        <AutoTextarea disabled={bloqueado} placeholder="Resultado / informe" value={vals['Resultado'] ?? ''} onChange={(e) => setVals({ ...vals, Resultado: e.target.value })} />
      )}
      <div className="flex items-center gap-2">
        <input disabled={bloqueado} className={`${fieldCls} h-9`} placeholder="URL del PDF del resultado (opcional)" value={url} onChange={(e) => setUrl(e.target.value)} />
        {item.item.resultado_url && <a href={item.item.resultado_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm whitespace-nowrap">Ver PDF</a>}
        {!bloqueado && <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>}
      </div>
      {/* solicitudId disponible para contexto futuro */}
      <input type="hidden" value={solicitudId} readOnly />
    </div>
  );
}

// ---- Gráfico evolutivo de un parámetro ----
function GraficoSerie({ solicitudId, parametro, onClose }: { solicitudId: string; parametro: string; onClose: () => void }) {
  // resolvemos el paciente desde la solicitud
  const { data: sol } = useQuery<SolicitudDetalle>({ queryKey: ['med-solicitud', solicitudId], queryFn: () => medRpc<SolicitudDetalle>('med_solicitud', { _id: solicitudId }) });
  const pacienteId = (sol?.solicitud as unknown as { paciente_id?: string })?.paciente_id;
  const { data: serie = [], isFetching } = useQuery<SerieRow[]>({
    queryKey: ['med-serie', solicitudId, parametro],
    queryFn: () => medRpc<SerieRow[]>('med_serie_resultados', { _paciente_id: pacienteId, _parametro: parametro }),
    enabled: !!pacienteId,
  });
  const data = serie.map((s) => ({ ...s, f: new Date(s.fecha).toLocaleDateString(LOCALE) }));
  const ref = serie.find((s) => s.valor_ref_min != null || s.valor_ref_max != null);

  return (
    <Modal title={`Evolución · ${parametro}`} onClose={onClose} wide>
      <div className="h-72">
        {isFetching ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          : data.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Sin datos históricos para «{parametro}».</p> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                {ref && ref.valor_ref_min != null && ref.valor_ref_max != null && (
                  <ReferenceArea y1={ref.valor_ref_min} y2={ref.valor_ref_max} fill="#22c55e" fillOpacity={0.1} />
                )}
                <XAxis dataKey="f" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="valor" name={parametro} stroke="#3b82f6" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          )}
      </div>
    </Modal>
  );
}
